/* eslint-disable react/no-unstable-nested-components, simple-import-sort/imports */
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { toNumber } from 'lodash';
import { Trash2 } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Button } from 'shadcn/components/ui/Button';
import { Input } from 'shadcn/components/ui/Input';
import { MultiSelect } from 'shadcn/components/ui/MultiSelect';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'shadcn/components/ui/Select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shadcn/components/ui/Table';
import { Tooltip, TooltipContent, TooltipTrigger } from 'shadcn/components/ui/tooltip';

import {
    usePurchaseOrderItemTypesQuery,
    useSourcingReasonsQuery,
    useUnpurchasedOutcomesQuery,
} from 'components/procurement/components/purchaseOrdersV2/queries/PurchaseOrderItemEditQueries';
import { formatPurchaseOrderDateForInput } from 'components/procurement/components/purchaseOrdersV2/utils/purchaseOrderDateUtils';
import {
    FALLBACK_TEXT,
    formatCurrency,
    formatNumber,
    normalizeOptionalText,
    normalizeStringList,
} from 'components/procurement/components/purchaseOrdersV2/utils/purchaseOrderDisplayUtils';

const FIELD_LABELS = {
    expectedExpirationDate: 'Expected Expiration',
    expectedProductionDate: 'Expected Production',
    qty: 'Case Quantity',
    vendorSku: 'Vendor SKU',
    csPallet: 'Cases per Pallet',
    unitPrice: 'Case Price',
    week: 'Week',
    year: 'Year',
    purchaseType: 'Purchase Type',
    suggestedRetailPrice: 'SRP',
    unpurchasedOutcome: 'Where Would It Go?',
    sourcingReasons: "Why I'm a Misfit",
    countryOfOriginId: 'Country of Origin',
};

/**
 * Displays a table of purchase order items in inline edit mode.
 *
 * @param {Object} props
 * @param {Object[]} props.rows - Array of purchase order item objects.
 * @param {Object[]} props.countries - Array of country objects.
 * @param {Object} props.itemEditStateRef - Ref storing uncontrolled input/select values keyed by row.
 * @param {Set<string|number>} props.removedItemIds - Set of PO map IDs marked for removal.
 * @param {() => void} props.onItemEditsChange - Callback invoked when any item edit changes (used to recompute derived edit state).
 * @returns {JSX.Element} Purchase order item edit table.
 */
export function PurchaseOrderItemEditTable({
    countries,
    itemEditStateRef,
    onItemEditsChange,
    onRemoveNewRow,
    removedItemIds,
    rows,
    setRemovedItemIds,
}) {
    const [, setLocalEditsVersion] = useState(0);

    // Avoid remounting cells (and losing focus) by reading changing state from refs.
    const removedItemIdsRef = useRef(removedItemIds);

    useEffect(() => {
        removedItemIdsRef.current = removedItemIds;
    }, [removedItemIds]);

    const { data: purchaseTypes = [] } = usePurchaseOrderItemTypesQuery({ enabled: true });
    const { data: unpurchasedOutcomes = [] } = useUnpurchasedOutcomesQuery({ enabled: true });
    const { data: sourcingReasons = [] } = useSourcingReasonsQuery({ enabled: true });

    const sourcingReasonOptions = useMemo(
        () => sourcingReasons.map((r) => ({ label: String(r), value: String(r) })),
        [sourcingReasons],
    );

    const countryOptions = useMemo(() => {
        if (!Array.isArray(countries)) return [];
        return countries
            .filter((c) => c?.id !== null && c?.id !== undefined)
            .map((c) => ({
                value: String(c.id),
                label: c?.code ? `${c?.name ?? c.id} (${c.code})` : String(c?.name ?? c.id),
            }));
    }, [countries]);

    const setInputValue = useCallback(
        (rowId, field, value) => {
            const state = itemEditStateRef?.current;
            if (!state) return;
            if (!state.inputs) state.inputs = {};
            if (!state.inputs[rowId]) state.inputs[rowId] = {};
            state.inputs[rowId][field] = value;
            setLocalEditsVersion((v) => v + 1);
            if (typeof onItemEditsChange === 'function') onItemEditsChange();
        },
        [itemEditStateRef, onItemEditsChange],
    );

    const setSelectValue = useCallback(
        (rowId, field, value) => {
            const state = itemEditStateRef?.current;
            if (!state) return;
            if (!state.selects) state.selects = {};
            if (!state.selects[rowId]) state.selects[rowId] = {};
            state.selects[rowId][field] = value;
            setLocalEditsVersion((v) => v + 1);
            if (typeof onItemEditsChange === 'function') onItemEditsChange();
        },
        [itemEditStateRef, onItemEditsChange],
    );

    const setMultiValue = useCallback(
        (rowId, field, value) => {
            const state = itemEditStateRef?.current;
            if (!state) return;
            if (!state.multi) state.multi = {};
            if (!state.multi[rowId]) state.multi[rowId] = {};
            state.multi[rowId][field] = value;
            setLocalEditsVersion((v) => v + 1);
            if (typeof onItemEditsChange === 'function') onItemEditsChange();
        },
        [itemEditStateRef, onItemEditsChange],
    );

    const getDraftValue = useCallback(
        (rowId, field, fallback) => {
            const raw = itemEditStateRef?.current?.inputs?.[rowId]?.[field];
            if (raw === undefined) return fallback;
            if (raw && typeof raw === 'object' && Object.prototype.hasOwnProperty.call(raw, 'value')) {
                return raw.value;
            }
            return raw;
        },
        [itemEditStateRef],
    );

    const toggleRemove = useCallback(
        (rowId) => {
            setRemovedItemIds((prev) => {
                const next = new Set(prev);
                const normalizedId = String(rowId);
                if (next.has(normalizedId)) next.delete(normalizedId);
                else next.add(normalizedId);
                // Keep render-time reads in sync immediately; otherwise the ref can lag a render behind.
                removedItemIdsRef.current = next;
                return next;
            });
        },
        [setRemovedItemIds],
    );

    const columnDefs = useMemo(() => {
        const columns = [
            {
                id: 'itemMasterSku',
                header: 'Item Master SKU',
                accessorFn: (row) => row?.sku ?? row?.itemMasterSku ?? row?.itemMasterID ?? null,
                cell: ({ getValue, row }) => {
                    const rowId = String(row.original.id);
                    const isRemoved = removedItemIdsRef.current?.has?.(rowId) === true;
                    return (
                        <span className={isRemoved ? 'line-through opacity-60' : ''}>
                            {normalizeOptionalText(getValue())}
                        </span>
                    );
                },
            },
            {
                id: 'name',
                header: 'Name',
                accessorFn: (row) => row?.itemName ?? row?.name ?? null,
                cell: ({ getValue, row }) => {
                    const rowId = String(row.original.id);
                    const value = normalizeOptionalText(getValue());
                    const isFallback = value === FALLBACK_TEXT;
                    const isRemoved = removedItemIdsRef.current?.has?.(rowId) === true;
                    const cellClassName = isRemoved ? 'line-through opacity-60' : '';

                    if (isFallback) {
                        return <span className={cellClassName}>{value}</span>;
                    }

                    return (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className={`block max-w-[240px] truncate text-left ${cellClassName}`}>
                                    {value}
                                </span>
                            </TooltipTrigger>
                            <TooltipContent side="top">{value}</TooltipContent>
                        </Tooltip>
                    );
                },
            },
            {
                id: 'expectedExpirationDate',
                header: FIELD_LABELS.expectedExpirationDate,
                cell: ({ row }) => {
                    const rowId = String(row.original.id);
                    const isRemoved = removedItemIdsRef.current?.has?.(rowId) === true;
                    const value =
                        formatPurchaseOrderDateForInput(
                            row.original?.expectedExpirationDate ?? row.original?.expiresOn,
                        ) ?? '';
                    return (
                        <Input
                            type="date"
                            defaultValue={value}
                            onChange={(e) => setInputValue(rowId, 'expectedExpirationDate', e.target.value)}
                            disabled={isRemoved}
                        />
                    );
                },
            },
            {
                id: 'expectedProductionDate',
                header: FIELD_LABELS.expectedProductionDate,
                cell: ({ row }) => {
                    const rowId = String(row.original.id);
                    const isRemoved = removedItemIdsRef.current?.has?.(rowId) === true;
                    const value = formatPurchaseOrderDateForInput(row.original?.expectedProductionDate) ?? '';
                    return (
                        <Input
                            type="date"
                            defaultValue={value}
                            onChange={(e) => setInputValue(rowId, 'expectedProductionDate', e.target.value)}
                            disabled={isRemoved}
                        />
                    );
                },
            },
            {
                id: 'qty',
                header: FIELD_LABELS.qty,
                cell: ({ row }) => {
                    const rowId = String(row.original.id);
                    const isRemoved = removedItemIdsRef.current?.has?.(rowId) === true;
                    const rawValue = row.original?.qty;
                    const value =
                        rawValue === null || rawValue === undefined || rawValue === '' ? '' : String(rawValue);
                    return (
                        <Input
                            type="number"
                            step="1"
                            inputMode="numeric"
                            defaultValue={value}
                            onChange={(e) => setInputValue(rowId, 'qty', e.target.value)}
                            disabled={isRemoved}
                        />
                    );
                },
            },
            {
                id: 'vendorSku',
                header: FIELD_LABELS.vendorSku,
                cell: ({ row }) => {
                    const rowId = String(row.original.id);
                    const isRemoved = removedItemIdsRef.current?.has?.(rowId) === true;
                    const value = row.original?.vendorSku ?? '';
                    return (
                        <Input
                            type="text"
                            defaultValue={value}
                            style={{ width: '150px' }}
                            onChange={(e) => setInputValue(rowId, 'vendorSku', e.target.value)}
                            disabled={isRemoved}
                        />
                    );
                },
            },
            {
                id: 'csPallet',
                header: FIELD_LABELS.csPallet,
                cell: ({ row }) => {
                    const rowId = String(row.original.id);
                    const isRemoved = removedItemIdsRef.current?.has?.(rowId) === true;
                    const rawValue = row.original?.csPallet;
                    const value =
                        rawValue === null || rawValue === undefined || rawValue === '' ? '' : String(rawValue);
                    return (
                        <Input
                            type="number"
                            step="1"
                            inputMode="numeric"
                            defaultValue={value}
                            onChange={(e) => setInputValue(rowId, 'csPallet', e.target.value)}
                            disabled={isRemoved}
                        />
                    );
                },
            },
            {
                id: 'unitPrice',
                header: FIELD_LABELS.unitPrice,
                cell: ({ row }) => {
                    const rowId = String(row.original.id);
                    const isRemoved = removedItemIdsRef.current?.has?.(rowId) === true;
                    const rawValue = row.original?.unitPrice ?? row.original?.casePrice;
                    const numericValue =
                        rawValue === null || rawValue === undefined || rawValue === '' ? null : Number(rawValue);
                    const value = Number.isFinite(numericValue) ? numericValue.toFixed(2) : '';
                    return (
                        <Input
                            type="text"
                            inputMode="decimal"
                            defaultValue={value}
                            onChange={(e) => setInputValue(rowId, 'unitPrice', e.target.value)}
                            disabled={isRemoved}
                            onBlur={(e) => {
                                const n = Number(e.target.value);
                                if (Number.isFinite(n)) {
                                    const next = n.toFixed(2);
                                    e.target.value = next;
                                    setInputValue(rowId, 'unitPrice', next);
                                }
                            }}
                        />
                    );
                },
            },
            {
                id: 'week',
                header: FIELD_LABELS.week,
                cell: ({ row }) => {
                    const rowId = String(row.original.id);
                    const isRemoved = removedItemIdsRef.current?.has?.(rowId) === true;
                    const rawValue = getDraftValue(rowId, 'week', row.original?.week);
                    const value =
                        rawValue === null || rawValue === undefined || rawValue === '' ? '' : String(rawValue);
                    return (
                        <Input
                            type="number"
                            step="1"
                            inputMode="numeric"
                            min="1"
                            max="53"
                            value={value}
                            onChange={(e) => setInputValue(rowId, 'week', e.target.value)}
                            disabled={isRemoved}
                            className="w-[88px] min-w-[88px]"
                        />
                    );
                },
            },
            {
                id: 'year',
                header: FIELD_LABELS.year,
                cell: ({ row }) => {
                    const rowId = String(row.original.id);
                    const isRemoved = removedItemIdsRef.current?.has?.(rowId) === true;
                    const rawValue = getDraftValue(rowId, 'year', row.original?.year);
                    const value =
                        rawValue === null || rawValue === undefined || rawValue === '' ? '' : String(rawValue);
                    return (
                        <Input
                            type="number"
                            step="1"
                            inputMode="numeric"
                            min="2000"
                            max="2100"
                            value={value}
                            onChange={(e) => setInputValue(rowId, 'year', e.target.value)}
                            disabled={isRemoved}
                            className="w-[96px] min-w-[96px]"
                        />
                    );
                },
            },
            {
                id: 'purchaseType',
                header: FIELD_LABELS.purchaseType,
                cell: ({ row }) => {
                    const rowId = String(row.original.id);
                    const isRemoved = removedItemIdsRef.current?.has?.(rowId) === true;
                    const value = String(row.original?.purchaseType ?? '');
                    return (
                        <Select
                            defaultValue={value || undefined}
                            onValueChange={(v) => setSelectValue(rowId, 'purchaseType', v)}
                            disabled={isRemoved}
                        >
                            <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select type" />
                            </SelectTrigger>
                            <SelectContent>
                                {purchaseTypes.map((type) => (
                                    <SelectItem key={type} value={String(type)}>
                                        {String(type)}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    );
                },
            },
            {
                id: 'suggestedRetailPrice',
                header: FIELD_LABELS.suggestedRetailPrice,
                cell: ({ row }) => {
                    const rowId = String(row.original.id);
                    const isRemoved = removedItemIdsRef.current?.has?.(rowId) === true;
                    const rawValue = row.original?.suggestedRetailPrice ?? row.original?.msrpPrice;
                    const numericValue =
                        rawValue === null || rawValue === undefined || rawValue === '' ? null : Number(rawValue);
                    const value = Number.isFinite(numericValue) ? numericValue.toFixed(2) : '';
                    return (
                        <Input
                            type="text"
                            inputMode="decimal"
                            defaultValue={value}
                            onChange={(e) => setInputValue(rowId, 'suggestedRetailPrice', e.target.value)}
                            disabled={isRemoved}
                            onBlur={(e) => {
                                const n = Number(e.target.value);
                                if (Number.isFinite(n)) {
                                    const next = n.toFixed(2);
                                    e.target.value = next;
                                    setInputValue(rowId, 'suggestedRetailPrice', next);
                                }
                            }}
                            className="w-[96px] min-w-[96px]"
                        />
                    );
                },
            },
            {
                id: 'unpurchasedOutcome',
                header: FIELD_LABELS.unpurchasedOutcome,
                cell: ({ row }) => {
                    const rowId = String(row.original.id);
                    const isRemoved = removedItemIdsRef.current?.has?.(rowId) === true;
                    const value = String(row.original?.unpurchasedOutcome ?? row.original?.whereWouldItGo ?? '');
                    return (
                        <Select
                            defaultValue={value || undefined}
                            onValueChange={(v) => setSelectValue(rowId, 'unpurchasedOutcome', v)}
                            disabled={isRemoved}
                        >
                            <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select outcome" />
                            </SelectTrigger>
                            <SelectContent>
                                {unpurchasedOutcomes.map((outcome) => (
                                    <SelectItem key={outcome} value={String(outcome)}>
                                        {String(outcome)}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    );
                },
            },
            {
                id: 'sourcingReasons',
                header: FIELD_LABELS.sourcingReasons,
                cell: ({ row }) => {
                    const rowId = String(row.original.id);
                    const isRemoved = removedItemIdsRef.current?.has?.(rowId) === true;
                    const value = normalizeStringList(row.original?.sourcingReasons);
                    return (
                        <MultiSelect
                            key={`${rowId}-${value.slice().sort().join('|')}`}
                            options={sourcingReasonOptions}
                            defaultValue={value}
                            maxCount={2}
                            modalPopover={false}
                            placeholder="None"
                            disabled={isRemoved}
                            onValueChange={(values) => setMultiValue(rowId, 'sourcingReasons', values)}
                        />
                    );
                },
            },
            {
                id: 'countryOfOriginId',
                header: FIELD_LABELS.countryOfOriginId,
                cell: ({ row }) => {
                    const rowId = String(row.original.id);
                    const isRemoved = removedItemIdsRef.current?.has?.(rowId) === true;
                    const originalId =
                        row.original?.countryOfOriginId ??
                        row.original?.countryOfOriginID ??
                        row.original?.countryOfOrigin ??
                        '';
                    const value = originalId != null ? String(originalId) : '';
                    return (
                        <Select
                            defaultValue={value || undefined}
                            onValueChange={(v) => setSelectValue(rowId, 'countryOfOriginId', v)}
                            disabled={isRemoved}
                        >
                            <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select country" />
                            </SelectTrigger>
                            <SelectContent>
                                {countryOptions.map((country) => (
                                    <SelectItem key={country.value} value={country.value}>
                                        {country.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    );
                },
            },
            {
                id: 'totalUnits',
                header: 'Total Units',
                cell: ({ row }) => {
                    const rowId = String(row.original.id);
                    const qty = toNumber(getDraftValue(rowId, 'qty', row.original?.qty));
                    const baseUnitsPerCase = toNumber(row.original?.baseUnitsPerCase ?? 0);
                    return formatNumber(qty * baseUnitsPerCase);
                },
            },
            {
                id: 'totalCost',
                header: 'Total Cost',
                cell: ({ row }) => {
                    const rowId = String(row.original.id);
                    const qty = toNumber(getDraftValue(rowId, 'qty', row.original?.qty));
                    const unitPrice = toNumber(
                        getDraftValue(rowId, 'unitPrice', row.original?.unitPrice ?? row.original?.casePrice),
                    );
                    return formatCurrency(qty * unitPrice);
                },
            },
            {
                id: 'remove',
                header: 'Remove',
                cell: ({ row }) => {
                    const rowId = String(row.original.id);
                    const isRemoved = removedItemIdsRef.current?.has?.(rowId) === true;
                    const isNewRow = rowId.startsWith('new-');
                    return (
                        <Button
                            type="button"
                            variant={isRemoved ? 'destructive' : 'ghost'}
                            size="iconSm"
                            onClick={() => {
                                if (isNewRow && typeof onRemoveNewRow === 'function') {
                                    onRemoveNewRow(rowId);
                                    return;
                                }
                                toggleRemove(rowId);
                            }}
                            aria-label={isRemoved ? 'Unmark removal' : 'Mark for removal'}
                        >
                            <Trash2 className="h-4 w-4" />
                        </Button>
                    );
                },
            },
        ];

        return columns;
    }, [
        countryOptions,
        getDraftValue,
        onRemoveNewRow,
        purchaseTypes,
        setInputValue,
        setMultiValue,
        setSelectValue,
        sourcingReasonOptions,
        toggleRemove,
        unpurchasedOutcomes,
    ]);

    const table = useReactTable({
        data: rows,
        columns: columnDefs,
        getCoreRowModel: getCoreRowModel(),
    });

    return (
        <Table className="rounded-md border border-border" style={{ minWidth: '1900px' }}>
            <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                            <TableHead
                                key={header.id}
                                className="whitespace-nowrap text-left text-xs font-semibold uppercase tracking-wide"
                            >
                                {header.isPlaceholder
                                    ? null
                                    : flexRender(header.column.columnDef.header, header.getContext())}
                            </TableHead>
                        ))}
                    </TableRow>
                ))}
            </TableHeader>
            <TableBody>
                {table.getRowModel().rows.map((row) => (
                    <TableRow
                        key={row.id}
                        className={
                            removedItemIdsRef.current?.has?.(String(row.original.id)) ? 'bg-muted/30' : undefined
                        }
                    >
                        {row.getVisibleCells().map((cell) => (
                            <TableCell key={cell.id} className="whitespace-pre-wrap break-words align-top text-sm">
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </TableCell>
                        ))}
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
}
